import _ from 'lodash';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as networkDeviceFilterId } from '../../filters/network-device-filter/NetworkDeviceFilter';
import { filterId as accessPointFilterId } from '../../filters/access-point-filter/AccessPointFilter';
import { filterId as wlcFilterId } from '../../filters/wireless-controller-filter/WirelessControllerFilter';
import { filterId as interfaceFilterId } from '../../filters/interface-filter/InterfaceFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { metricDefFormatter } from '../../../utilities';
import { listTooltip } from './funcs/IpmList';
import {
    interfacePriorityFilterId,
    interfaceTypeFilterId,
    siteFilterId,
    statusFilterId,
    timeRangeFilterId,
} from '../../filters';

const title = t`Interfaces`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'interface',
        entityFields: ['name', 'ifType', 'duplex', 'deviceName', 'deviceIpAddress', 'ifDescription'],
        metrics: ['status', 'avgNetworkIn', 'avgNetworkOut', 'errorsTotal', 'discardsTotalPercent'],
        trends: [
            'status',
            'avgNetworkIn',
            'avgNetworkOut',
            'discardsInPercent',
            'discardsOutPercent',
            'errorsIn',
            'errorsOut',
            'inBitsPerSec',
            'outBitsPerSec',
            'packetsInRate',
            'packetsOutRate',
        ],
    },
};

const COLOR_NETWORK_IN = '#a069db';
const COLOR_NETWORK_OUT = '#55BDE0';

const percentConfig = {
    units: '%',
    multiplier: 1,
    forceDecimals: true,
};

const errorsSparkLinkFn = () => {
    const chartTrends = [
        {
            color: COLOR_NETWORK_IN,
            key: 'errorsIn',
            label: 'Errors In',
            units: ' Errors/Min',
            multiplier: 0,
        },
        {
            color: COLOR_NETWORK_OUT,
            key: 'errorsOut',
            label: 'Errors Out',
            units: ' Errors/Min',
            multiplier: 0,
        },
    ];
    return (rowData) => ({
        chartDef: {
            chartTrends,
        },
        data: rowData,
    });
};

const discardsSparkLinkFn = () => {
    const chartTrends = [
        {
            color: COLOR_NETWORK_IN,
            key: 'discardsInPercent',
            label: 'Discards In',
            ...percentConfig,
        },
        {
            color: COLOR_NETWORK_OUT,
            key: 'discardsOutPercent',
            label: 'Discards Out',
            ...percentConfig,
        },
    ];
    return (rowData) => ({
        chartDef: {
            chartTrends,
        },
        data: rowData,
    });
};

const linker = (row) => {
    const selector = _.get(row, 'device.deviceType');
    let target;
    let filterId;
    switch (selector) {
        case 'accesspoint':
            target = '/access-point-detail';
            filterId = accessPointFilterId;
            break;
        case 'wirelesscontroller':
            target = '/wireless-controller-detail';
            filterId = wlcFilterId;
            break;
        default:
            target = '/device-detail';
            filterId = networkDeviceFilterId;
    }
    return {
        target,
        mappedFilterParams: {
            [filterId]: 'interface.device',
        },
    };
};

const ERRORS = t`Errors`;
const DISCARDS = t`Discards`;

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Interface`,
        sortable: true,
        mapping: 'interface.name',
        getTooltip: listTooltip('interface.name', 'interface.description'),
        width: 200,
        link: {
            target: '/interface-detail',
            mappedFilterParams: {
                [interfaceFilterId]: 'interface',
            },
        },
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Device`,
        sortable: true,
        mapping: 'interface.deviceName',
        getTooltip: listTooltip('interface.device.name', 'interface.device.description'),
        width: 120,
        link: linker,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: 'Description',
        sortable: true,
        mapping: 'interface.description',
        width: 120,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: 'Util In',
        sortable: true,
        mapping: 'avgNetworkIn',
        width: 95,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: 'Util Out',
        sortable: true,
        mapping: 'avgNetworkOut',
        width: 95,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: ERRORS, // associated with Errors column
        csvTitle: ERRORS,
        mapping: 'errorsTotal',
        width: 80,
        formatter: metricDefFormatter({ units: '', multiplier: 0 }),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: ERRORS,
        sortable: true,
        sortProperty: 'errorsTotal',
        width: 100,
        mapper: (rowData) => errorsSparkLinkFn()(rowData),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: DISCARDS, // associated with Discards column
        csvTitle: DISCARDS,
        mapping: 'discardsTotalPercent',
        width: 60,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: DISCARDS,
        sortProperty: 'discardsTotalPercent',
        sortable: true,
        width: 100,
        mapper: (rowData) => discardsSparkLinkFn()(rowData),
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
        minWidth: 240,
    },
];

const sorters = [{ property: 'status', direction: 'desc' }];

function InterfaceListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="interface.deleted"
        />
    );
}

InterfaceListPanel.propTypes = panelPropTypes;

InterfaceListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'InterfaceList',
    catalogEntry: {
        category: PANEL_CATEGORIES.INTERFACE,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of interfaces, showing status trends`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [
            networkDeviceFilterId,
            interfacePriorityFilterId,
            interfaceTypeFilterId,
            siteFilterId,
            statusFilterId,
        ],
    },
})(InterfaceListPanel);
